import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AccessControlGroupResponse,
  AccessControlGroupType,
  SiteResponse,
} from "api/ApiClient";
import { apiClientFactory } from "helpers";
import { EllipsisVertical, Filter, LockKeyhole, Search } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import {
  GROUPS_VIEW,
  setGroupsViewPreference,
} from "shared/utils/groupsViewPreference";
import RegisterGroupModal from "./components/RegisterGroupModal";

type AdminAccessControlGroup = AccessControlGroupResponse & {
  siteId?: string;
  systemGroupId?: string;
};

function GroupRowActions({
  groupId,
  onDeleteClick,
}: {
  groupId: string;
  onDeleteClick: () => void;
}) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Group actions"
              className="inline-flex items-center justify-center rounded-md size-8 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              type="button"
            >
              <EllipsisVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Group actions</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to={`/admin/accessControlGroups/${groupId}`}>View group details</Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={onDeleteClick}
        >
          Delete group
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminGroups({
  page,
  setPage,
  pageSize,
  setPageSize,
  search,
  setSearch,
  typeFilter,
  setTypeFilter,
}: {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  search: string;
  setSearch: (search: string) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
}) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [propertyExternalIdSearch, setPropertyExternalIdSearch] = React.useState("");
  const propertyExternalIdFilter = propertyExternalIdSearch.trim() || undefined;

  const { data, isLoading } = useQuery({
    queryKey: ["acg", "list", page, pageSize, search, propertyExternalIdFilter],
    queryFn: () =>
      apiClientFactory().getAccessControlGroups(
        page,
        pageSize,
        search || undefined,
        undefined,
        propertyExternalIdFilter,
      ),
  });

  const typedGroups = React.useMemo(() => (data || []) as AdminAccessControlGroup[], [data]);

  const tenantExternalIds = React.useMemo(() => {
    const uniqueTenantExternalIds = new Set<string>();

    typedGroups.forEach((group) => {
      ((group.tenants as any[]) || []).forEach((tenantLink) => {
        const externalId = tenantLink?.tenant?.externalId;
        if (externalId) {
          uniqueTenantExternalIds.add(externalId);
        }
      });
    });

    return Array.from(uniqueTenantExternalIds);
  }, [typedGroups]);

  const { data: sites = [] } = useQuery({
    queryKey: ["acg", "list", "sites", tenantExternalIds.join(",")],
    queryFn: async () => {
      const siteSets = await Promise.all(
        tenantExternalIds.map((tenantExternalId) =>
          apiClientFactory().getSites(undefined, undefined, undefined, tenantExternalId),
        ),
      );

      return siteSets.flat() as SiteResponse[];
    },
    enabled: tenantExternalIds.length > 0,
  });

  const siteNameById = React.useMemo(() => {
    const mapping: Record<string, string> = {};

    sites.forEach((site) => {
      if (site.id && site.name) {
        mapping[site.id] = site.name;
      }
    });

    return mapping;
  }, [sites]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClientFactory().deleteAccessControlGroup(id);
    },
    onSuccess: async () => {
      setDeleteId(null);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["acg", "list"] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message || e?.message || "Unable to delete group.");
    },
  });

  const groups = React.useMemo(() => {
    let filteredGroups = typedGroups;

    if (typeFilter) {
      filteredGroups = filteredGroups.filter((group) => group.type === typeFilter);
    }

    return filteredGroups;
  }, [typedGroups, typeFilter]);

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            Access Control Groups
          </h1>
          <Link
            to="/admin/accessControlGroups-legacy"
            onClick={() => setGroupsViewPreference(GROUPS_VIEW.LEGACY)}
            className="inline-block mt-1 text-sm underline text-slate-500 underline-offset-2 hover:text-slate-700"
          >
            Switch to legacy view
          </Link>
        </div>
        <Button className="mt-4 sm:mt-0" onClick={() => setAddOpen(true)} type="button">
          Add Group
        </Button>
      </header>

      {error ? <Alert variant="destructive">{error}</Alert> : null}

      <section
        aria-labelledby="groups-list-heading"
        className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200"
      >
        <div className="flex flex-col gap-4 px-4 py-4 border-b border-slate-200 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 id="groups-list-heading" className="text-base font-semibold text-slate-900">
              Groups
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {isLoading ? "Loading groups" : `${groups.length} groups on this page`}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="relative block w-full sm:w-64">
              <span className="sr-only">Search groups</span>
              <Search
                aria-hidden="true"
                className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-slate-400"
              />
              <input
                className="w-full h-10 py-2 pr-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 pl-9 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Search groups"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </label>
            <label className="relative block w-full sm:w-56">
              <span className="sr-only">Filter by property external ID</span>
              <input
                className="w-full h-10 px-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Property external ID"
                value={propertyExternalIdSearch}
                onChange={(event) => {
                  setPropertyExternalIdSearch(event.target.value);
                  setPage(0);
                }}
              />
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm font-medium transition-colors bg-white border rounded-md border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                >
                  <Filter className="size-4" />
                  {typeFilter || "All Types"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Filter by type</DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => {
                    setTypeFilter("");
                    setPage(0);
                  }}
                >
                  All Types
                </DropdownMenuItem>
                {Object.values(AccessControlGroupType).map((type) => (
                  <DropdownMenuItem
                    key={type}
                    onSelect={() => {
                      setTypeFilter(type);
                      setPage(0);
                    }}
                  >
                    {type}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-3 text-sm min-h-80 text-slate-500">
            Loading groups
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left min-w-200">
              <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">Name</th>
                  <th scope="col" className="px-4 py-3">Type</th>
                  <th scope="col" className="px-4 py-3">Property External ID</th>
                  <th scope="col" className="px-4 py-3">System Group ID</th>
                  <th scope="col" className="px-4 py-3">Site</th>
                  <th scope="col" className="px-4 py-3 text-right w-14">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100 text-slate-700">
                {groups.map((group) => (
                  <tr key={group.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-4 py-3 align-middle">
                      <Link
                        to={`/admin/accessControlGroups/${group.id}`}
                        className="font-medium text-slate-900 hover:text-blue-700 hover:underline"
                      >
                        {group.name || group.id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <Badge variant="secondary">{group.type || "Unknown"}</Badge>
                    </td>
                    <td className="px-4 py-3 align-middle text-slate-500">
                      {group.propertyExternalId || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs align-middle text-slate-500">
                      {group.systemGroupId || "—"}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {group.siteId ? (
                        <span className="inline-flex items-center gap-1.5">
                          <LockKeyhole className="size-4 text-slate-400" />
                          {siteNameById[group.siteId] || group.siteId}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-right align-middle">
                      {group.id ? (
                        <GroupRowActions
                          groupId={group.id}
                          onDeleteClick={() => setDeleteId(group.id || null)}
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-sm text-center text-slate-500">
                      No groups found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          visibleRowCount={groups.length}
          isLoading={isLoading}
          disableNext={typedGroups.length < pageSize}
          onNext={() => setPage(page + 1)}
          onPrevious={() => setPage(Math.max(0, page - 1))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      </section>

      <RegisterGroupModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={async () => {
          setAddOpen(false);
          await queryClient.invalidateQueries({ queryKey: ["acg", "list"] });
        }}
      />

      <Dialog
        open={!!deleteId}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !deleteMutation.isPending) {
            setDeleteId(null);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="sm:max-w-xs"
          onEscapeKeyDown={(event) => {
            if (deleteMutation.isPending) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (deleteMutation.isPending) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Access Control Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this access control group?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button disabled={deleteMutation.isPending} onClick={() => setDeleteId(null)} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={deleteMutation.isPending || !deleteId}
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              type="button"
              variant="destructive"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
